<template>
  <!-- EcountEtl 운영 대시보드 (SPEC-ECOUNT-ETL-OPS-CONSOLE-001 REQ-OC-DASHBOARD-001~005) -->
  <div class="page-container-v2">
    <div class="page-header">
      <span class="page-title">{{ $t('ecountEtl_dashboard_title') }}</span>
      <div style="display: flex; align-items: center; gap: 8px; margin-left: auto;">
        <timezone-toggle :current-tz="currentTz" @toggle="toggleTz" />
        <refresh-toggle
          :enabled="autoRefreshEnabled"
          :last-fetch-at="lastFetchAt"
          :loading="loading"
          :seconds-until-next="secondsUntilNext"
          @toggle="onToggleAutoRefresh"
          @force-refresh="fetchAll"
        />
        <a-button size="small" @click="showShortcutHelpRef = true" :title="$t('ecountEtl_dashboard_shortcutHint')">?</a-button>
      </div>
    </div>

    <!-- KR / CN 탭 -->
    <a-tabs v-model:activeKey="activeTab" style="margin-bottom: 16px;">
      <a-tab-pane key="kr" :tab="$t('ecountEtl_dashboard_kr')" />
      <a-tab-pane key="cn" :tab="$t('ecountEtl_dashboard_cn')" />
    </a-tabs>

    <!-- 통계 카드 4개 — REQ-OC-DASHBOARD-001: products.total / transactions.total / orphan / gtinChecksumFail -->
    <a-row :gutter="16" style="margin-bottom: 24px;">
      <a-col :xs="24" :sm="12" :lg="6">
        <a-card :loading="loading">
          <a-statistic
            :title="$t('ecountEtl_dashboard_productsTotal')"
            :value="currentSummary.productsTotal ?? 0"
          />
        </a-card>
      </a-col>
      <a-col :xs="24" :sm="12" :lg="6">
        <a-card :loading="loading">
          <a-statistic
            :title="$t('ecountEtl_dashboard_transactionsTotal')"
            :value="currentSummary.transactionsTotal ?? 0"
          />
        </a-card>
      </a-col>
      <a-col :xs="24" :sm="12" :lg="6">
        <a-card :loading="loading">
          <a-statistic
            :title="$t('ecountEtl_dashboard_orphan')"
            :value="currentSummary.orphan ?? 0"
            :value-style="(currentSummary.orphan ?? 0) > 0 ? { color: '#fa8c16' } : {}"
          />
        </a-card>
      </a-col>
      <a-col :xs="24" :sm="12" :lg="6">
        <a-card :loading="loading">
          <a-statistic
            :title="$t('ecountEtl_dashboard_gtinChecksumFail')"
            :value="currentSummary.gtinChecksumFail ?? 0"
            :value-style="(currentSummary.gtinChecksumFail ?? 0) > 0 ? { color: '#f5222d' } : {}"
          />
        </a-card>
      </a-col>
    </a-row>

    <!-- Phase β: 스케줄 설정 토글 패널 (SPEC-ECOUNT-ETL-CRON-CONSOLE-001) -->
    <sync-toggle-panel
      @refresh="fetchAll"
      @toast="onSyncToast"
    />

    <!-- 관측성 3 패널 제거 (REQ-DEDUP-002) — SPEC-ECOUNT-ADMIN-CONSOLE-OBSERVABILITY-MERGE-001 -->
    <!-- @MX:NOTE: [AUTO] SessionPanel/OapiQuotaBadge/BatchHealthPanel + useObservability를 EcountErpConsole.vue로 단일화 -->
    <!-- 중복 polling 방지: 관측성 GET은 ERP 콘솔에서만 1배 동작 (REQ-DEDUP-003) -->

    <!-- CN sentinel 안내 -->
    <a-alert
      v-if="activeTab === 'cn'"
      type="info"
      :message="$t('ecountEtl_dashboard_cnSentinel')"
      style="margin-bottom: 16px;"
      show-icon
    />

    <!-- 오류 표시 -->
    <a-alert
      v-if="error"
      type="error"
      :message="error"
      style="margin-bottom: 16px;"
      show-icon
      closable
      @close="error = null"
    />

    <!-- 최근 실행 목록 -->
    <a-card :loading="loading">
      <template #title>
        {{ $t('ecountEtl_dashboard_recentRuns') }}
      </template>
      <template #extra>
        <a-button type="link" size="small" @click="goToRuns">
          {{ $t('ecountEtl_dashboard_viewAll') }}
        </a-button>
      </template>
      <a-table
        :data-source="recentRuns"
        :columns="runsColumns"
        :pagination="false"
        size="small"
        row-key="runId"
        :scroll="{ x: 700 }"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'status'">
            <run-status-badge :value="record.status" />
          </template>
          <template v-if="column.key === 'startedAt'">
            {{ formatTime(record.startedAt) }}
          </template>
          <template v-if="column.key === 'actions'">
            <a-button size="small" type="link" @click="goToRunDetail(record.runId)">
              {{ $t('ecountEtl_runs_detail') }}
            </a-button>
          </template>
        </template>
      </a-table>
    </a-card>

    <!-- 단축키 도움말 -->
    <shortcut-help v-model:visible="showShortcutHelpRef" />
  </div>
</template>

<script>
// @MX:SPEC: SPEC-ECOUNT-ETL-OPS-CONSOLE-001 REQ-OC-DASHBOARD-001~005
// @MX:ANCHOR: [AUTO] 대시보드 데이터 패치 진입점 — KR/CN 탭 분기 + auto-refresh 30초 + 통계 카드 4개
// @MX:REASON: [AUTO] 4개 통계 카드와 최근 실행 테이블이 이 화면에 집중, fan_in >= 3
// @MX:NOTE: [AUTO] SPEC-ECOUNT-ADMIN-CONSOLE-OBSERVABILITY-MERGE-001: 관측성 3 패널을 EcountErpConsole.vue로 단일화.
// useObservability / SessionPanel / OapiQuotaBadge / BatchHealthPanel / isSuperAdmin 이 파일에서 제거.
// 관측성 polling은 ERP 콘솔에서만 1배 동작 (중복 polling 방지, REQ-DEDUP-002/003).

import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { useAutoRefresh } from './composables/useAutoRefresh.js';
import { useShortcuts } from './composables/useShortcuts.js';
import { useKstTime } from './composables/useKstTime.js';
import { getSummary, getCnSummary, getRuns, extractApiError } from '../../apis/ecountEtl.js';
import RunStatusBadge from './_components/RunStatusBadge.vue';
import RefreshToggle from './_components/RefreshToggle.vue';
import TimezoneToggle from './_components/TimezoneToggle.vue';
import ShortcutHelp from './_components/ShortcutHelp.vue';
import SyncTogglePanel from './components/SyncTogglePanel.vue';
import { RouterEnum } from '../../enums/router.enums.js';

export default {
  name: 'EcountEtlDashboard',
  components: {
    RunStatusBadge, RefreshToggle, TimezoneToggle, ShortcutHelp, SyncTogglePanel,
  },

  setup() {
    const { currentTz, toggleTz, format } = useKstTime();
    const {
      enabled: autoRefreshEnabled,
      toggle: toggleAutoRefresh,
      lastFetchAt,
      secondsUntilNext,
      forceRefresh,
    } = useAutoRefresh({ intervalMs: 30000, storageKey: 'ecountEtl.autoRefresh' });

    // @MX:NOTE: [AUTO] setup()에서 useRouter()로 라우터 참조 — Options API + setup() 혼용 시
    // this.$router 접근 불가이므로 useRouter() 클로저로 캡처
    const router = useRouter();
    // showShortcutHelp를 ref로 setup에서 관리 — 단축키 콜백이 클로저로 안전하게 캡처
    const showShortcutHelpRef = ref(false);

    useShortcuts({
      r: () => forceRefresh(),
      '?': () => { showShortcutHelpRef.value = true; },
      'g+d': () => router.push({ name: RouterEnum.EcountEtl.Dashboard.name }),
      'g+r': () => router.push({ name: RouterEnum.EcountEtl.Runs.name }),
      'g+x': () => router.push({ name: RouterEnum.EcountEtl.DeadLetters.name }),
      'g+p': () => router.push({ name: RouterEnum.EcountEtl.Products.name }),
      'g+t': () => router.push({ name: RouterEnum.EcountEtl.Transactions.name }),
    });

    return {
      currentTz, toggleTz, format,
      autoRefreshEnabled, toggleAutoRefresh, lastFetchAt, secondsUntilNext, forceRefresh,
      showShortcutHelpRef,
    };
  },

  data() {
    return {
      activeTab: 'kr',
      loading: false,
      error: null,
      // @MX:NOTE: [AUTO] showShortcutHelp는 setup()의 showShortcutHelpRef와 v-model로 연결
      // template에서는 showShortcutHelpRef를 직접 사용
      krSummary: {},
      cnSummary: {},
      recentRuns: [],
    };
  },

  computed: {
    // @MX:NOTE: [AUTO] markets.CN = -1 sentinel — CN 카운트는 getCnSummary() 로 별도 집계
    currentSummary() {
      return this.activeTab === 'kr' ? this.krSummary : this.cnSummary;
    },
    runsColumns() {
      return [
        { key: 'runId', dataIndex: 'runId', title: this.$t('ecountEtl_runs_col_runId'), width: 80 },
        { key: 'jobType', dataIndex: 'jobType', title: this.$t('ecountEtl_runs_col_jobType') },
        { key: 'status', dataIndex: 'status', title: this.$t('ecountEtl_runs_col_status'), width: 120 },
        { key: 'startedAt', dataIndex: 'startedAt', title: this.$t('ecountEtl_runs_col_startedAt') },
        { key: 'actions', title: this.$t('ecountEtl_runs_col_actions'), width: 80 },
      ];
    },
  },

  watch: {
    activeTab() {
      // 탭 전환 시 해당 탭 데이터 패치
      this.fetchAll();
    },
    forceRefresh(fn) {
      // forceRefresh 트리거 연결
      if (typeof fn === 'function') fn();
    },
  },

  mounted() {
    this.fetchAll();
    // auto-refresh 콜백 등록
    this.$watch(
      () => this.autoRefreshEnabled,
      () => {},
      { immediate: false },
    );
  },

  methods: {
    onToggleAutoRefresh(val) {
      this.toggleAutoRefresh(val);
    },
    /**
     * SyncTogglePanel 토스트 이벤트 처리
     * @param {{ type: string, message: string }} payload
     */
    onSyncToast({ type, message }) {
      // Ant Design Vue 글로벌 message 대신 화면 상단 a-alert로 1초 표시
      // @MX:NOTE: [AUTO] $message가 전역 주입되어 있으면 사용, 없으면 console.log fallback
      if (this.$message) {
        if (type === 'success') this.$message.success(message);
        else if (type === 'warning') this.$message.warning(message);
        else this.$message.error(message);
      } else {
        // eslint-disable-next-line no-console
        console.log(`[SyncToast][${type}]`, message);
      }
    },
    /**
     * getSummary/getCnSummary 응답을 카드 필드로 정규화
     * 백엔드 확정 플랫 필드명 직독: productsTotal / transactionsTotal / orphan / gtinChecksumFail
     * REQ-OC-DASHBOARD-001 + 백엔드 계약 동시 수정 기준 (R-2 수정)
     */
    normalizeSummary(raw) {
      if (!raw) return {};
      // @MX:NOTE: [AUTO] 중첩 fallback(raw.products?.total 등) 제거 — 백엔드가 플랫 필드로 확정 납품
      return {
        productsTotal:      raw.productsTotal      ?? 0,
        transactionsTotal:  raw.transactionsTotal  ?? 0,
        orphan:             raw.orphan             ?? 0,
        gtinChecksumFail:   raw.gtinChecksumFail   ?? 0,
      };
    },
    toggleTzHandler() {
      this.toggleTz();
    },
    async fetchAll() {
      this.loading = true;
      this.error = null;
      try {
        if (this.activeTab === 'kr') {
          const [summaryRes, runsRes] = await Promise.all([
            getSummary(),
            getRuns({ limit: 10, market: 'KR' }),
          ]);
          // @MX:NOTE: [AUTO] markets.CN = -1 이므로 KR summary에서 CN 카운트를 직접 쓰지 않음
          // REQ-OC-DASHBOARD-001: products.total/transactions.total/orphan/gtinChecksumFail 매핑
          this.krSummary = this.normalizeSummary(summaryRes.data?.kr ?? summaryRes.data);
          this.recentRuns = runsRes.data?.items ?? runsRes.data ?? [];
        } else {
          // CN 탭: getCnSummary() 로 별도 집계
          const [cnRes, runsRes] = await Promise.all([
            getCnSummary(),
            getRuns({ limit: 10, market: 'CN' }),
          ]);
          this.cnSummary = this.normalizeSummary(cnRes.data);
          this.recentRuns = runsRes.data?.items ?? runsRes.data ?? [];
        }
      } catch (e) {
        const err = extractApiError(e);
        const localized = this.$t(err.messageKey, err.messageParams);
        this.error = localized.startsWith('ecountEtl_') ? err.message : localized;
      } finally {
        this.loading = false;
      }
    },
    formatTime(value) {
      return this.format(value);
    },
    goToRuns() {
      this.$router.push({ name: RouterEnum.EcountEtl.Runs.name });
    },
    goToRunDetail(runId) {
      this.$router.push({ name: RouterEnum.EcountEtl.RunDetail.name, params: { runId } });
    },
  },
};
</script>
